/**
 * Single source for broker submission / quote workflow status:
 * - API values on `status` and/or `brokerWorkflowStatus` (prefer `brokerWorkflowStatus` when set)
 * - Accepts PascalCase, snake_case, kebab-case, and spaced phrases (normalized internally)
 * - Display labels for list pills, detail badge, compare shell
 * - Quote compare route vs submission modal (see isQuoteCompareWorkflowStatus)
 */

/** Lowercase key: spaces, underscores, and hyphens removed (API format differences). */
function normKey(raw) {
  if (raw == null || typeof raw !== "string") return "";
  return raw
    .trim()
    .toLowerCase()
    .replaceAll(/[\s_-]+/g, "");
}

const REQUESTED_LIKE = new Set(["requested", "pendingsubmission", "submitted"]);

const TERMINAL_STATUS_LABELS = {
  withdrawn: "Withdrawn",
  declined: "Declined",
  requestcancelled: "Cancelled",
  submissionfailed: "Failed",
  failed: "Failed",
  cancelledcoverage: "Cancelled",
};

// A Failed submission has a terminal label but is not archived: it stays in
// progress so the broker can open it and Resubmit (S2CWB-333).
const FAILED_LIKE = new Set(["failed", "submissionfailed"]);

// Every carrier declined. Still terminal (archived-like, In force / Declined
// lane, archived pill) but labelled "Declined" so the broker sees why.
const DECLINED_LIKE = new Set(["declined", "submissiondeclined"]);

const ARCHIVED_LIKE = new Set([
  ...Object.keys(TERMINAL_STATUS_LABELS).filter((key) => !FAILED_LIKE.has(key)),
  ...DECLINED_LIKE,
]);

/** Includes generatingquotes for “still in early workflow” checks. */
const REQUESTED_SEMANTIC = new Set([...REQUESTED_LIKE, "generatingquotes"]);

export const SUBMISSION_BOARD_LANES = [
  { id: "in_progress", label: "In progress" },
  { id: "quote", label: "Quote" },
  { id: "binding", label: "Binding" },
  { id: "in_force_declined", label: "In force / Declined" },
];

function boardTerminalStatusLabel(raw) {
  return TERMINAL_STATUS_LABELS[normKey(raw)] || null;
}

/**
 * Prefer `brokerWorkflowStatus`; fall back to `status` when empty.
 * @param {{ status?: string, brokerWorkflowStatus?: string } | null | undefined} row
 * @returns {string}
 */
export function submissionStatusRawFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const a = row.status;
  const b = row.brokerWorkflowStatus;
  const pick = (v) => (v != null && String(v).trim() !== "" ? String(v).trim() : "");
  const primary = pick(b);
  if (primary) return primary;
  return pick(a);
}

/**
 * Human-readable submission / workflow status for UI.
 * @param {string} raw
 * @returns {string}
 */
export function formatSubmissionStatus(raw) {
  if (!raw || typeof raw !== "string") return "—";
  let trimmed = raw.trim();
  if (!trimmed) return "—";
  if (trimmed.toLowerCase().startsWith("submission ")) {
    const rest = trimmed.slice("submission ".length).trim();
    return formatSubmissionStatus(rest);
  }
  const s = normKey(trimmed);
  // A submission UWB refused, could not be reached for, or could not process
  // (S2CWB-333). Shown as Failed, never Declined, and it can be resubmitted.
  if (s === "failed" || s === "submissionfailed") return "Failed";
  if (s === "generatingquotes") return "In progress";
  if (s === "reviewing" || s === "reviewingquotes") return "Quote";
  if (s === "quotesready" || s === "quoteready") return "Quote";
  if (s === "quote" || s === "quotes") return "Quote";
  if (REQUESTED_LIKE.has(s)) return "Requested";
  if (DECLINED_LIKE.has(s)) return "Declined";
  if (ARCHIVED_LIKE.has(s)) return "Archived";
  if (s === "binding" || s === "bindrequested") return "Binding";
  if (s === "bound" || s === "completed" || s === "inforce") return "In force";
  return trimmed
    .replaceAll(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * @param {{ status?: string, brokerWorkflowStatus?: string } | null | undefined} row
 * @returns {string}
 */
export function formatSubmissionStatusFromRow(row) {
  return formatSubmissionStatus(submissionStatusRawFromRow(row));
}

export function normalizeStatus(raw) {
  return formatSubmissionStatus(raw);
}

export function isRequestedLike(raw) {
  if (!raw || typeof raw !== "string") return false;
  return REQUESTED_SEMANTIC.has(normKey(raw));
}

export function isArchivedLike(raw) {
  if (!raw || typeof raw !== "string") return false;
  return ARCHIVED_LIKE.has(normKey(raw));
}

/**
 * Lane id for the broker submissions board (reuses formatSubmissionStatus labels).
 * @param {string} raw
 * @returns {"in_progress"|"quote"|"binding"|"in_force_declined"}
 */
export function submissionBoardLane(raw) {
  const label = formatSubmissionStatus(raw).toLowerCase();
  if (label === "archived" || label === "declined" || label === "in force") return "in_force_declined";
  if (label === "binding") return "binding";
  if (label === "quote") return "quote";
  return "in_progress";
}

/**
 * @param {{ status?: string, brokerWorkflowStatus?: string } | null | undefined} row
 * @returns {"in_progress"|"quote"|"binding"|"in_force_declined"}
 */
export function submissionBoardLaneFromRow(row) {
  return submissionBoardLane(submissionStatusRawFromRow(row));
}

export function formatSubmissionTerminalStatusLabel(raw) {
  if (!raw || typeof raw !== "string") return "—";
  let trimmed = raw.trim();
  if (!trimmed) return "—";
  if (trimmed.toLowerCase().startsWith("submission ")) {
    trimmed = trimmed.slice("submission ".length).trim();
  }
  return boardTerminalStatusLabel(trimmed) || formatSubmissionStatus(trimmed);
}

/**
 * Row click → quote compare page when status (after formatSubmissionStatus) is
 * Quote-like or Binding-like. Matches pillClass / product rules.
 * @param {string} displayLabel — output of formatSubmissionStatus for the row
 * @returns {boolean}
 */
/**
 * A Failed submission (S2CWB-333): shows its reason and offers Resubmit.
 * @param {string} raw — a raw status or a formatted label
 * @returns {boolean}
 */
export function isFailedLike(raw) {
  if (!raw || typeof raw !== "string") return false;
  return FAILED_LIKE.has(normKey(raw));
}

export function isQuoteCompareWorkflowStatus(displayLabel) {
  const s = String(displayLabel || "").toLowerCase();
  return s === "quote" || s === "quotes";
}

/**
 * Row click → bind package / policy docs panel when status is Binding or In force.
 * @param {string} displayLabel — output of formatSubmissionStatus for the row
 * @returns {boolean}
 */
export function isBindPackageWorkflowStatus(displayLabel) {
  const s = String(displayLabel || "").toLowerCase();
  return s === "binding" || s === "in force";
}

/**
 * Maps formatted status label to pill style key (panel maps to bq-pill--* classes).
 * @param {string} displayLabel
 * @returns {"quote"|"bind"|"inForce"|"requested"|"archived"|"muted"}
 */
export function submissionStatusPillVariant(displayLabel) {
  const s = String(displayLabel || "").toLowerCase();
  if (s === "quote" || s === "quotes") return "quote";
  if (s === "binding") return "bind";
  if (s === "in force") return "inForce";
  if (s === "requested") return "requested";
  if (s === "archived" || s === "declined") return "archived";
  if (s === "failed") return "failed";
  return "muted";
}

export function submissionStatusPillClass(displayLabel, { terminal = false } = {}) {
  if (terminal) {
    return "submission-status-pill submission-status-pill--terminal";
  }
  const v = submissionStatusPillVariant(displayLabel);
  const byVariant = {
    quote: "submission-status-pill--quote",
    bind: "submission-status-pill--bind",
    inForce: "submission-status-pill--in-force",
    requested: "submission-status-pill--requested",
    archived: "submission-status-pill--terminal",
    failed: "submission-status-pill--terminal",
    muted: "submission-status-pill--muted",
  };
  return `submission-status-pill ${byVariant[v] || "submission-status-pill--muted"}`;
}
